/**
 * DCP CalibrationIlluminant code → CIE 1931 chromaticity (xy) lookup.
 *
 * Adobe DNG 1.7 spec, section 6.4.18 (CalibrationIlluminant1/2).
 * The codes match EXIF LightSource (tag 37384). Adobe's DNG SDK uses
 * these to interpolate ColorMatrix1 ↔ ColorMatrix2 by image white-balance.
 *
 * We only ship the codes Adobe DCPs actually use in practice (StdA,
 * D55, D65, D75, plus the daylight/tungsten/fluor/flash/cloudy/shade
 * subset). Unknown codes resolve to D65 (the safest default).
 *
 * Source: CIE Standard Illuminants xy chromaticities, 2° observer
 * (per CIE 15:2018 publication + Adobe DNG 1.7 spec section 6.4.18).
 *
 * @module src/pipeline/dcp/illuminant
 */

/* CIE 1931 (2° observer) xy chromaticities for the EXIF illuminant codes.
 * Frozen so callers can't accidentally mutate (matches our codebase's
 * immutability rule). */
const ILLUMINANT_XY = Object.freeze({
    /* 0  = unknown — resolve to D65 */
    1:  Object.freeze([0.3457, 0.3585]),  /* Daylight (~D50)        */
    2:  Object.freeze([0.3721, 0.3751]),  /* Fluorescent (F2 cool white) */
    3:  Object.freeze([0.4476, 0.4074]),  /* Tungsten / incandescent (~A) */
    4:  Object.freeze([0.3721, 0.3751]),  /* Flash — Adobe maps to D55 in practice; safer to treat as cool */
    9:  Object.freeze([0.3324, 0.3474]),  /* Fine weather */
    10: Object.freeze([0.3457, 0.3585]),  /* Cloudy weather */
    11: Object.freeze([0.3175, 0.3296]),  /* Shade */
    12: Object.freeze([0.3805, 0.3769]),  /* Daylight fluorescent (D 5700-7100) */
    13: Object.freeze([0.3737, 0.3725]),  /* Day white fluorescent */
    14: Object.freeze([0.3719, 0.3751]),  /* Cool white fluorescent (F2) */
    15: Object.freeze([0.4099, 0.3936]),  /* White fluorescent */
    17: Object.freeze([0.4476, 0.4074]),  /* Standard light A */
    18: Object.freeze([0.3484, 0.3516]),  /* Standard light B */
    19: Object.freeze([0.3101, 0.3162]),  /* Standard light C */
    20: Object.freeze([0.3324, 0.3474]),  /* D55 */
    21: Object.freeze([0.3127, 0.3290]),  /* D65 */
    22: Object.freeze([0.2990, 0.3149]),  /* D75 */
    23: Object.freeze([0.3457, 0.3585]),  /* D50 */
    24: Object.freeze([0.4091, 0.3940]),  /* ISO studio tungsten */
});

/* Default illuminant when the DCP is missing one or has an unknown code. */
const DEFAULT_ILLUMINANT_XY = Object.freeze([0.3127, 0.3290]); /* D65 */

/**
 * Look up CIE xy for an EXIF illuminant code.
 *
 * @param {number} code        EXIF LightSource code (1..24, others rare)
 * @returns {readonly [number, number]}  Frozen [x, y] tuple
 */
export function illuminantToXY(code) {
    const xy = ILLUMINANT_XY[code | 0];
    return xy || DEFAULT_ILLUMINANT_XY;
}

/**
 * Given a CIE xy, return a 0..1 weight `t` that linearly interpolates
 * between two reference illuminants by correlated colour temperature
 * (Adobe DNG SDK convention: weight is on the inverse-CCT axis, but
 * for our v1 we use a simple xy distance — measurably close in the
 * D55..D65 range that 95% of DCP profiles cover, and avoids McCamy's
 * cubic CCT approximation cost).
 *
 * Returns 0 → use illum1 fully; 1 → use illum2 fully.
 *
 * @param {readonly [number, number]} xy        Source illuminant (image WB)
 * @param {readonly [number, number]} xy1       ColorMatrix1's illuminant
 * @param {readonly [number, number]} xy2       ColorMatrix2's illuminant
 * @returns {number}  weight t ∈ [0, 1]
 */
export function interpWeight(xy, xy1, xy2) {
    const dx12 = xy2[0] - xy1[0];
    const dy12 = xy2[1] - xy1[1];
    const len2 = dx12 * dx12 + dy12 * dy12;
    if (len2 < 1e-12) return 0;
    const dx = xy[0] - xy1[0];
    const dy = xy[1] - xy1[1];
    const t = (dx * dx12 + dy * dy12) / len2;
    /* Clamp — DNG SDK does NOT extrapolate outside the calibration
     * range (would produce nonsense for tungsten on a D65/D75 profile). */
    if (t <= 0) return 0;
    if (t >= 1) return 1;
    return t;
}
